import { useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridReadyEvent, ICellRendererParams, IDatasource } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { Pencil, Trash2 } from 'lucide-react';
import { useStore } from '../../../../hooks/useStore';
import { observer } from 'mobx-react-lite';
import { IUserManagementResult } from '../../../../models/user-management/UserManagementModel';
import { localeText } from '../../../../constants/AgGridLocale';
import styles from '../UserManagement.module.css';

const defaultColDef = {
  sortable: true,
  resizable: true,
  suppressMovable: true,
  filter: 'agTextColumnFilter',
  filterParams: {
    filterOptions: ['contains'],
    defaultOption: 'contains',
    buttons: ['apply', 'reset'],
    closeOnApply: true,
    suppressAndOrCondition: true,
    maxNumConditions: 1,
  },
};

const ActionCellRenderer = (props: ICellRendererParams<IUserManagementResult>) => {
  const { popupStore, userManagementStore } = useStore();

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data) {
      userManagementStore.setSelectedUser(props.data);
      popupStore.setIsEditUserOpen(true);
    }
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (props.data) {
      userManagementStore.setSelectedUser(props.data);
      popupStore.setIsDeleteUserConfirmationOpen(true);
    }
  };

  return (
    <div className={styles.actionCell}>
      <button onClick={handleEdit} className={styles.editButton} title="Düzenle">
        <Pencil size={20} />
      </button>
      <button onClick={handleDelete} className={styles.deleteButton} title="Sil">
        <Trash2 size={20} />
      </button>
    </div>
  );
};

const UsersTab = () => {
  const { userManagementStore, uiStore } = useStore();

  const dataSource = useMemo((): IDatasource => {
    return {
      getRows: async params => {
        try {
          uiStore.setLoading(true);
          const result = await userManagementStore.listUsers({
            startRow: params.startRow,
            endRow: params.endRow,
            filterModel: params.filterModel,
            sortModel: params.sortModel,
          });
          if (result) {
            params.successCallback(result.rows, result.lastRow);
          } else {
            params.failCallback();
          }
        } catch (error) {
          console.error('Data loading error:', error);
          params.failCallback();
        } finally {
          uiStore.setLoading(false);
        }
      },
    };
  }, [userManagementStore, uiStore]);

  const onGridReady = useCallback(
    (params: GridReadyEvent<IUserManagementResult>) => {
      userManagementStore.setGridApi(params.api);
    },
    [userManagementStore]
  );

  const columnDefs = useMemo<ColDef<IUserManagementResult>[]>(
    () => [
      {
        headerName: 'İşlemler',
        width: 120,
        pinned: 'left',
        sortable: false,
        filter: false,
        cellRenderer: ActionCellRenderer,
        cellStyle: () => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '0 8px',
          backgroundColor: 'transparent',
        }),
      },
      { field: 'id', headerName: 'ID', width: 100, filter: false },
      { field: 'userName', headerName: 'Kullanıcı Adı', minWidth: 150, flex: 1, filter: 'agTextColumnFilter' },
      { field: 'email', headerName: 'E-posta', minWidth: 200, flex: 1.5, filter: 'agTextColumnFilter' },
      {
        field: 'roles',
        headerName: 'Roller',
        minWidth: 150,
        flex: 1,
        filter: false,
        valueFormatter: (params: any) => (params.value ? params.value.join(', ') : ''),
      },
      {
        field: 'isActive',
        headerName: 'Aktif?',
        minWidth: 100,
        valueFormatter: (params: any) => (params.value ? 'Evet' : 'Hayır'),
      },
      {
        field: 'createdAt',
        headerName: 'Oluşturulma',
        minWidth: 160,
        valueFormatter: (params: any) =>
          params.value ? new Date(params.value).toLocaleString('tr-TR') : '',
      },
    ],
    []
  );

  return (
    <div className="ag-theme-alpine" style={{ height: '100%', width: '100%' }}>
      <AgGridReact
        rowModelType="infinite"
        cacheBlockSize={20}
        maxBlocksInCache={1}
        datasource={dataSource}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        onGridReady={onGridReady}
        pagination={true}
        paginationPageSize={20}
        rowSelection="multiple"
        rowHeight={48}
        headerHeight={48}
        animateRows={true}
        suppressColumnVirtualisation={true}
        suppressRowVirtualisation={true}
        localeText={localeText}
      />
    </div>
  );
};

export default observer(UsersTab);
